import type { Edge, XYPosition } from "@xyflow/react";
import type {
  AgentProfileRecord,
  Capability,
  FabricResource,
  TaskSummary,
  VirtualKey,
  VirtualModel
} from "../../lib/schemas";
import { normalizeState } from "../../lib/schemas";
import { nodeRegistry } from "./nodeRegistry";
import {
  capabilityUpstreamModel,
  channelModelCapabilities,
  channelModelSummary
} from "./channelModels";
import { llmAdapterLabel, llmModels } from "./endpointConfiguration";
import type { CanvasKind, CanvasPort, ControlCanvasNode } from "./types";

interface CanvasProjectionInput {
  resources: FabricResource[];
  virtualModels: VirtualModel[];
  profiles: AgentProfileRecord[];
  keys: VirtualKey[];
  tasks: TaskSummary[];
  positions: Record<string, XYPosition>;
  compact: boolean;
}

export interface CanvasProjection {
  nodes: ControlCanvasNode[];
  edges: Edge[];
}

const columns: Record<CanvasKind, number> = {
  llm: 0,
  mcp: 0,
  a2a: 0,
  discovery: 0,
  model: 1,
  agent: 2,
  key: 3,
  task: 4
};

function port(kind: CanvasKind): CanvasPort {
  return { id: kind, label: nodeRegistry[kind].shortLabel, kind };
}

function defaultPosition(
  kind: CanvasKind,
  laneIndex: number,
  globalIndex: number,
  compact: boolean
): XYPosition {
  if (compact) {
    return { x: 40 + (globalIndex % 2) * 270, y: 80 + Math.floor(globalIndex / 2) * 158 };
  }
  const column = columns[kind];
  return {
    x: 80 + column * 330,
    y: 90 + laneIndex * 154 + (column % 2) * 42
  };
}

function node(
  id: string,
  kind: CanvasKind,
  laneIndex: number,
  globalIndex: number,
  title: string,
  subtitle: string,
  status: string,
  resource: ControlCanvasNode["data"]["resource"],
  positions: Record<string, XYPosition>,
  compact: boolean
): ControlCanvasNode {
  return {
    id,
    type: "control",
    position: positions[id] ?? defaultPosition(kind, laneIndex, globalIndex, compact),
    data: {
      kind,
      title,
      subtitle,
      status,
      resource,
      inputs: kind === "discovery" ? [] : [port(kind)],
      outputs: nodeRegistry[kind].derives.map(port)
    }
  };
}

function canvasKindFor(resource: FabricResource): CanvasKind | null {
  switch (resource.kind) {
    case "channel":
      return "llm";
    case "mcp_server":
      return "mcp";
    case "a2a_endpoint":
      return "a2a";
    default:
      return null;
  }
}

function resourceIdentity(resource: FabricResource): Set<string> {
  return new Set([resource.id, resource.name]);
}

function channelModelAliases(resource: FabricResource): Set<string> {
  const names = new Set<string>();
  for (const model of llmModels(resource.configuration)) {
    if (model.public_name) names.add(model.public_name);
    if (model.upstream_name) names.add(model.upstream_name);
  }
  for (const capability of channelModelCapabilities(resource)) {
    names.add(capability.namespace);
    names.add(capabilityUpstreamModel(capability));
  }
  return names;
}

function grantEdge(source: string, target: string, label: string, animated = false): Edge {
  return {
    id: `${source}:${target}:${label}`,
    source,
    target,
    label,
    type: "smoothstep",
    className: `canvas-edge canvas-edge-${label}`,
    animated
  };
}

export function projectCanvas(input: CanvasProjectionInput): CanvasProjection {
  const nodes: ControlCanvasNode[] = [];
  const edges: Edge[] = [];
  const perColumn = new Map<number, number>();
  const capabilityById = new Map<string, Capability>();
  const resourceById = new Map(input.resources.map((resource) => [resource.id, resource]));
  const addNode = (
    id: string,
    kind: CanvasKind,
    title: string,
    subtitle: string,
    status: string,
    resource: ControlCanvasNode["data"]["resource"]
  ) => {
    const column = columns[kind];
    const laneIndex = perColumn.get(column) ?? 0;
    perColumn.set(column, laneIndex + 1);
    nodes.push(
      node(
        id,
        kind,
        laneIndex,
        nodes.length,
        title,
        subtitle,
        status,
        resource,
        input.positions,
        input.compact
      )
    );
  };

  for (const resource of input.resources) {
    for (const capability of resource.capabilities) capabilityById.set(capability.id, capability);
    const kind = canvasKindFor(resource);
    if (!kind) continue;
    const modelSummary = channelModelSummary(resource);
    const subtitle =
      resource.kind === "channel"
        ? `${llmAdapterLabel(resource.configuration)} / ${String(modelSummary.active)} active${modelSummary.pending > 0 ? ` / ${String(modelSummary.pending)} pending` : ""}`
        : `revision ${String(resource.active_revision)}`;
    addNode(
      `resource:${resource.id}`,
      kind,
      resource.name,
      `${resource.kind.replaceAll("_", " ").toUpperCase()} / ${subtitle}`,
      "configured",
      { type: "resource", value: resource }
    );
  }
  for (const virtualModel of input.virtualModels) {
    addNode(
      `model:${virtualModel.id}`,
      "model",
      virtualModel.public_name,
      `${String(virtualModel.routes.length)} ordered route${virtualModel.routes.length === 1 ? "" : "s"}`,
      "active",
      { type: "virtual_model", value: virtualModel }
    );
  }
  for (const record of input.profiles) {
    addNode(
      `agent:${record.resource_id}`,
      "agent",
      record.profile.name,
      `${record.profile.trust_domain} / v${String(record.version)}`,
      "configured",
      { type: "agent", value: record }
    );
  }
  for (const key of input.keys) {
    addNode(
      `key:${key.id}`,
      "key",
      key.prefix,
      key.revoked_at
        ? "Revoked"
        : `v${key.scopes.models.length > 0 ? String(key.scopes.models.length) : ""} model scopes`,
      key.revoked_at ? "revoked" : "active",
      { type: "key", value: key }
    );
  }
  for (const task of input.tasks) {
    addNode(
      `task:${task.id}`,
      "task",
      task.id.slice(0, 13),
      `${task.public_model} / ${new Date(task.updated_at).toLocaleDateString()}`,
      normalizeState(task.status),
      { type: "task", value: task }
    );
  }
  for (const resource of input.resources) {
    for (const capability of channelModelCapabilities(resource)) {
      if (capability.enabled) continue;
      addNode(
        `discovery:${capability.id}`,
        "discovery",
        capability.namespace,
        `${capability.kind} / ${capability.resource_name}`,
        "pending",
        { type: "discovery", value: capability }
      );
    }
  }

  for (const record of input.profiles) {
    const target = `agent:${record.resource_id}`;
    for (const resource of input.resources) {
      const identity = resourceIdentity(resource);
      const aliases =
        resource.kind === "channel" ? channelModelAliases(resource) : new Set<string>();
      const grants =
        resource.kind === "channel"
          ? record.profile.model_grants
          : resource.kind === "mcp_server"
            ? record.profile.tool_grants
            : record.profile.delegation_grants;
      if (grants.some((grant) => identity.has(grant) || aliases.has(grant))) {
        edges.push(grantEdge(`resource:${resource.id}`, target, "grant"));
      }
    }
    for (const virtualModel of input.virtualModels) {
      if (record.profile.model_grants.includes(virtualModel.public_name)) {
        edges.push(grantEdge(`model:${virtualModel.id}`, target, "grant"));
      }
    }
  }
  for (const virtualModel of input.virtualModels) {
    for (const route of virtualModel.routes) {
      if (route.target_kind === "capability") {
        const capability = capabilityById.get(route.target_id);
        if (capability && resourceById.has(capability.resource_id)) {
          edges.push(
            grantEdge(`resource:${capability.resource_id}`, `model:${virtualModel.id}`, "routes")
          );
        }
      } else {
        if (nodes.some((candidate) => candidate.id === `agent:${route.target_id}`)) {
          edges.push(grantEdge(`agent:${route.target_id}`, `model:${virtualModel.id}`, "routes"));
        }
      }
    }
  }
  for (const key of input.keys) {
    for (const record of input.profiles) {
      if (
        key.scopes.agents.includes(record.resource_id) ||
        key.scopes.agents.includes(record.profile.name)
      ) {
        edges.push(grantEdge(`agent:${record.resource_id}`, `key:${key.id}`, "scope"));
      }
    }
  }
  return { nodes, edges };
}
